import { FuuzeButton, FuuzeColors, formatSecondsToHMS, type SpotlightLink } from '@fuuze/shared';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { IoPlay } from 'react-icons/io5';
import { LuExternalLink } from 'react-icons/lu';

type Props = {
  spotlight: SpotlightLink;
  onPlay: (spotlight: SpotlightLink) => Promise<void>;
  customStyles?: {
    cardWrapper?: CSSProperties;
    spotlightWrapper?: CSSProperties;
  };
};

export function SpotlightCard({ spotlight, onPlay, customStyles }: Props) {
  const { t } = useTranslation();

  const openLink = (spotlight: SpotlightLink) => {
    window.open(spotlight.url, '_blank');
  };

  return (
    <div style={{ ...styles.cardWrapper, ...customStyles?.cardWrapper }}>
      <span>{formatSecondsToHMS(spotlight.start)}</span>

      <article style={styles.contentWrapper}>
        {spotlight.preview?.image && <img style={styles.cover} src={spotlight.preview?.image} />}
        <div style={styles.textWrapper}>
          {spotlight.preview?.siteName && <span style={styles.siteName}>{spotlight.preview.siteName}</span>}
          {spotlight.preview?.title && <span style={styles.title}>{spotlight.preview.title}</span>}
          {spotlight.preview?.description && <span style={styles.description}>{spotlight.preview.description}</span>}
        </div>
        <div style={styles.actionsWrapper}>
          <FuuzeButton scaleOnHover outline style={styles.action} onClick={() => onPlay(spotlight)}>
            <IoPlay />
            {t('common.play')}
          </FuuzeButton>
          <FuuzeButton scaleOnHover style={styles.action} onClick={() => openLink(spotlight)}>
            <LuExternalLink stroke='black' />
            {t('components.playerCollapsed.spotlightsDrawer.openLink')}
          </FuuzeButton>
        </div>
      </article>
    </div>
  );
}

const styles = {
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: FuuzeColors.background,
    borderRadius: '.5rem',
    width: 400,
  },
  cover: {
    width: '100%',
    objectFit: 'cover',
    maxHeight: 150,
    borderRadius: '.5rem .5rem 0 0',
  },
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
    padding: 8,
  },
  actionsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    margin: '8px 0 16px 0',
  },
  action: {
    display: 'flex',
    flexDirection: 'row',
    gap: '.5rem',
    alignItems: 'center',
  },
  siteName: {
    textTransform: 'uppercase',
    color: FuuzeColors.dark.dark700,
    fontWeight: 'bold',
    fontSize: 'small',
  },
  title: {
    fontWeight: 'bold',
  },
  description: {
    color: FuuzeColors.dark.dark700,
    fontSize: 'smaller',
  },
} satisfies Record<string, CSSProperties>;
